import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import TwoColumnLayout from "../../components/layouts/TwoColumnLayout";
import ListItem from "../../components/list-item/ListItem";
import {
  ProjectMenuBar,
  ProjectMenu,
} from "../../components/project/ProjectMenu";
import leftColumnList from "./ProjectConfigLeft";
import rightColumnList from "./ProjectConfigRight";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { isProducerGroupPosition, isPrimaryClientPosition, isResponsibleProducerPosition } from "../../utils/memberPositionUtils";
import { useShortcutAction } from "../../utils/keyboardShortcuts";

const ProjectConfig = () => {
  const { projectId } = useParams();
  const [selectedItem, setSelectedItem] = useState("projectTotalShots");
  const [layoutSetShowRightColumn, setLayoutSetShowRightColumn] = useState(null);

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData, isLoading: isLoadingMembership } = useGetProjectMembershipQuery(
    projectId,
    { skip: !projectId || !currentUserData }
  );

  const userPosition = membershipData?.data?.position;
  const hasEditPermission =
    userPosition &&
    (isProducerGroupPosition(userPosition) || isPrimaryClientPosition(userPosition));

  // TwoColumnLayoutからsetShowRightColumn関数を受け取るコールバック
  const getSetShowRightColumn = useCallback((setShowRightColumnFunc) => {
    setLayoutSetShowRightColumn(() => setShowRightColumnFunc);
  }, []);

  const renderListItems = (items) => {
    return items.map((item) => (
      <ListItem
        key={item.id}
        name={item.name}
        status={item.status}
        alert={item.alert}
        onClick={item.onClick}
        size={item.size}
        isSelected={selectedItem === item.id}
      />
    ));
  };

  // タスクボード共有設定は責任者プロデューサー・管理者のみメニューに表示する。
  const canManageTaskBoard = userPosition
    ? isResponsibleProducerPosition(userPosition)
    : false;

  // leftColumnListに渡すpropsオブジェクトを生成
  const leftColumnProps = {
    setSelectedItem,
    setShowRightColumn: value => {
      if (layoutSetShowRightColumn) {
        layoutSetShowRightColumn(value);
      }
    },
    canManageTaskBoard,
  };

  // ⌥6 再押下（既に INFO 表示中）: 表示中メニュー（チェックポイント →
  // 全体スケジュール → タグ → …）を定義順に循環する。末尾の次は先頭へ。
  useShortcutAction("config-cycle", () => {
    const ids = leftColumnList(leftColumnProps).map((item) => item.id);
    if (ids.length === 0) return;
    const nextId = ids[(ids.indexOf(selectedItem) + 1) % ids.length];
    setSelectedItem(nextId);
    // メニュークリック時と同様に右カラム（詳細）を表示状態にする
    if (layoutSetShowRightColumn) {
      layoutSetShowRightColumn(true);
    }
  });

  // 左カラムのコンテンツ（最上部に全タブ共通のプロジェクトメニュー行を置く）
  const leftColumnContent = (
    <>
      <ProjectMenuBar />
      {renderListItems(leftColumnList(leftColumnProps))}
    </>
  );

  // 右カラムのコンテンツ
  const rightColumnContent = rightColumnList[selectedItem];

  if (isLoadingMembership) return null;

  if (!hasEditPermission) {
    return (
      <div className="u-col-center u-w100 u-h100 u-gap8" style={{ padding: "64px 16px" }}>
        <span className="material-symbols-rounded" style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}>lock</span>
        <p className="bodytext u-text-light-gray">この機能はプロデューサーまたはクライアント責任者のみ利用できます。</p>
      </div>
    );
  }

  return (
    <TwoColumnLayout
      leftColumnContent={leftColumnContent}
      rightColumnContent={rightColumnContent}
      initialShowRightColumn={true}
      getSetShowRightColumn={getSetShowRightColumn}
      hasProjectBanner={true}
      // 楽曲照合結果は表（minWidth 1260px）を含むため右カラムを全幅にする
      wideRightColumn={selectedItem === "projectAcr"}
      // 2 カラム時はプロジェクトメニュー行が左カラム幅止まりになるため、
      // プロジェクトメニューはビューポート右端へ出す
      topRightOverlay={<ProjectMenu />}
    />
  );
};

export default ProjectConfig;
